import { DeviceInfo, DeviceStatus, DeviceStatistics, DeviceEvents, IDevice } from '../types/device';
import { TcpConnection } from './connections/TcpConnection';

export class Device implements IDevice {
  // 基础属性
  private readonly id: string;
  private readonly ipAddress: string;
  private readonly name: string;
  private readonly tcpPort: number;
  private readonly udpPort: number;
  
  // 连接管理
  private _tcpConnection: TcpConnection | null = null;
  private _audioReceiveWs: WebSocket | null = null;
  private _audioSendWs: WebSocket | null = null;
  
  // 状态
  private _isOnline: boolean = false;
  private _lastSeen: number = 0;
  
  // 音频状态
  private _audioEnabled: boolean = false;
  private _pttActive: boolean = false;
  
  // 统计信息
  private _statistics: DeviceStatistics = {
    packetsReceived: 0,
    packetsSent: 0,
    bytesReceived: 0,
    bytesSent: 0
  };
  
  constructor(deviceInfo: DeviceInfo) {
    this.id = deviceInfo.id;
    this.ipAddress = deviceInfo.ipAddress;
    this.name = deviceInfo.name || `${deviceInfo.ipAddress}:${deviceInfo.tcpPort}`;
    this.tcpPort = deviceInfo.tcpPort;
    this.udpPort = deviceInfo.udpPort;
    
    this.initializeConnections();
  }
  
  private initializeConnections(): void {
    this._tcpConnection = new TcpConnection(this.ipAddress, this.tcpPort);
    
    this._tcpConnection.on('connected', () => {
      this._isOnline = true;
      this._lastSeen = Date.now();
      this.emit('statusChanged', { device: this, online: true });
    });
    
    this._tcpConnection.on('disconnected', () => {
      this._isOnline = false;
      this._audioEnabled = false;
      this._pttActive = false;
      this.emit('statusChanged', { device: this, online: false });
    });
  }
  
  // 连接管理
  public async connect(): Promise<void> {
    try {
      await this._tcpConnection?.connect();
    } catch (error) {
      console.error('设备连接失败:', error);
      throw error;
    }
  }
  
  public async disconnect(): Promise<void> {
    await this._tcpConnection?.disconnect();
    await this.stopAudio();
    await this.stopPtt();
  }

  /**
   * 从外部更新设备连接状态（基于API获取的数据）
   */
  public updateConnectionStatus(isConnected: boolean): void {
    this._tcpConnection?.updateConnectionStatus(isConnected);
  }
  
  // 音频管理 - 已禁用直接WebSocket创建，使用新的音频架构
  public async startAudio(): Promise<void> {
    console.warn('Device.startAudio() 已禁用，请使用新的音频架构 (AudioManager)');
    // 这个方法已被新的音频架构替代，不再直接创建WebSocket连接
    // 音频管理现在由 AudioManager 统一处理
  }
  
  public async stopAudio(): Promise<void> {
    console.warn('Device.stopAudio() 已禁用，请使用新的音频架构 (AudioManager)');
    // 这个方法已被新的音频架构替代
  }
  
  // PTT管理 - 已禁用直接WebSocket创建，使用新的音频架构
  public async startPtt(): Promise<void> {
    console.warn('Device.startPtt() 已禁用，请使用新的音频架构 (AudioManager)');
    // 这个方法已被新的音频架构替代，不再直接创建WebSocket连接
    // PTT管理现在由 AudioManager 统一处理
  }
  
  public async stopPtt(): Promise<void> {
    console.warn('Device.stopPtt() 已禁用，请使用新的音频架构 (AudioManager)');
    // 这个方法已被新的音频架构替代
  }
  
  public sendPttAudio(audioData: Blob | ArrayBuffer): void {
    if (this._audioSendWs && this._pttActive) {
      this._audioSendWs.send(audioData);
      this._statistics.packetsSent++;
      this._statistics.bytesSent += audioData instanceof Blob ? audioData.size : audioData.byteLength;
    }
  }
  
  // 状态查询
  public isOnline(): boolean {
    return this._isOnline;
  }
  
  public isAudioEnabled(): boolean {
    return this._audioEnabled;
  }
  
  public isPttActive(): boolean {
    return this._pttActive;
  }
  
  public getStatus(): DeviceStatus {
    // 根据实际连接状态返回设备状态
    if (!this._isOnline) {
      return DeviceStatus.OFFLINE;
    }
    
    // 如果设备在线，检查是否正在被使用（PTT激活）
    if (this._pttActive) {
      return DeviceStatus.IN_USE;
    }
    
    return DeviceStatus.ONLINE;
  }
  
  // 设备信息
  public getInfo(): DeviceInfo {
    return {
      id: this.id,
      ipAddress: this.ipAddress,
      tcpPort: this.tcpPort,
      udpPort: this.udpPort,
      name: this.name
    };
  }

  // 获取设备ID（便捷方法）
  public getId(): string {
    return this.id;
  }

  // 获取设备ID（数字类型，用于API调用）
  public getDeviceId(): number {
    return parseInt(this.id) || 0;
  }
  
  public getDisplayName(): string {
    // 后端API只返回在线设备，所以不需要显示离线状态
    return `${this.ipAddress}:${this.tcpPort}`;
  }
  
  public getStatistics(): DeviceStatistics {
    return { ...this._statistics };
  }
  
  // 事件系统
  private eventListeners: Map<string, Function[]> = new Map();
  
  public on<K extends keyof DeviceEvents>(event: K, callback: (data: DeviceEvents[K]) => void): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event)!.push(callback);
  }
  
  public off<K extends keyof DeviceEvents>(event: K, callback: (data: DeviceEvents[K]) => void): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }
  
  private emit<K extends keyof DeviceEvents>(event: K, data: DeviceEvents[K]): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.forEach(callback => callback(data));
    }
  }
  
  // 资源清理
  public async dispose(): Promise<void> {
    await this.disconnect();
    this.eventListeners.clear();
  }
}
